<script setup lang="ts">
    import { Checkbox } from '@/components/ui/checkbox';
    import { CheckboxGroupRoot, useFilter } from 'reka-ui';
    import { Label } from '@/components/ui/label';
    import { PermissionOption } from '@/types';
    import { ref, computed } from 'vue';
    import { Separator } from '@/components/ui/separator';
    import { usePermissionOptions } from '@/composables/usePermissionOptions';
    import Loading from '@/components/Loading.vue';
    import SearchInput from '@/components/SearchInput.vue';

    const { permissionsOptions, isPlaceholderDataPermissionsOptions } = usePermissionOptions();

    const props = defineProps<{
        form: any;
    }>();

    const searchTerm = ref('');
    const { contains } = useFilter({ sensitivity: 'base' });

    const filteredPermissions = computed(() =>
        permissionsOptions.value.filter((option: PermissionOption) =>
            contains(option.label, searchTerm.value),
        ),
    );

    const allSelected = computed({
        get: () => {
            const totalOptions = permissionsOptions.value.reduce(
                (total: number, permission: PermissionOption) => total + permission.options.length,
                0,
            );
            return props.form.permission_ids.length === totalOptions;
        },
        set: () => toggleAll(),
    });

    const toggleAll = () => {
        if (allSelected.value) {
            props.form.permission_ids = [];
        } else {
            props.form.permission_ids = permissionsOptions.value.flatMap(
                (permission: PermissionOption) => permission.options.map((option) => option.value),
            );
        }
    };

    const isGroupSelected = (permission: PermissionOption) => {
        return permission.options.every((option) =>
            props.form.permission_ids.includes(option.value),
        );
    };

    const toggleGroup = (permission: PermissionOption) => {
        if (isGroupSelected(permission)) {
            props.form.permission_ids = props.form.permission_ids.filter(
                (id: number) => !permission.options.some((option) => option.value === id),
            );
        } else {
            props.form.permission_ids = [
                ...props.form.permission_ids,
                ...permission.options.map((option) => option.value),
            ];
        }
    };
</script>

<template>
    <div class="flex flex-col gap-2">
        <div class="flex flex-col gap-2">
            <Label>{{ $t('roles.select.permissions.placeholder') }}</Label>
            <SearchInput
                :placeholder="$t('roles.select.permissions.search_options.default')"
                v-model="searchTerm"
                :disabled="isPlaceholderDataPermissionsOptions"
            />
        </div>
        <div
            v-if="!isPlaceholderDataPermissionsOptions"
            class="flex flex-row items-center gap-2"
        >
            <Label class="flex flex-row items-center gap-2 font-normal">
                <Checkbox v-model="allSelected" />
                <span>{{
                    allSelected
                        ? $t('roles.select.texts.unselectAll')
                        : $t('roles.select.texts.selectAll')
                }}</span>
            </Label>
            <Separator
                orientation="vertical"
                class="h-2.5!"
            />
            <span class="text-xs font-bold">
                {{ form.permission_ids.length }} {{ $t('roles.select.texts.selectedOptions') }}
            </span>
        </div>
        <div
            v-if="!isPlaceholderDataPermissionsOptions"
            class="mt-6 flex max-h-96 flex-col gap-6 overflow-y-auto"
        >
            <div v-for="permission in filteredPermissions">
                <Label
                    class="text-secondary border-secondary mb-2 flex flex-row items-center gap-2 border-b pb-2 font-normal"
                >
                    {{ permission.label }}
                </Label>
                <div class="grid grid-cols-5 gap-2">
                    <Label class="flex flex-row items-center gap-2 font-normal">
                        <Checkbox
                            :model-value="isGroupSelected(permission)"
                            @update:model-value="() => toggleGroup(permission)"
                        />
                        <span class="text-muted-foreground">{{
                            isGroupSelected(permission)
                                ? $t('roles.select.texts.unselectAll')
                                : $t('roles.select.texts.selectAll')
                        }}</span>
                    </Label>
                    <CheckboxGroupRoot
                        v-for="option in permission.options"
                        v-model="form.permission_ids"
                    >
                        <Label class="flex flex-row items-center gap-2 font-normal">
                            <Checkbox :value="option.value" />
                            <span>{{ option.label }}</span>
                        </Label>
                    </CheckboxGroupRoot>
                </div>
            </div>
        </div>
        <Loading v-if="isPlaceholderDataPermissionsOptions" />
    </div>
</template>
